import { Document, FilePdf, CheckCircle, XCircle, AlertErrorCircle } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { IconButton } from '@/components/icon-button/IconButton'
import clsx from 'clsx'
import { TimelineFileStatus } from '../../constants'

type TimelineFileCardProps = {
  fileName: string
  // The Add Files page only ever shows in-flight upload states; EXTRACTED is reserved for the
  // (read-only) roster elsewhere.
  status: Exclude<TimelineFileStatus, TimelineFileStatus.EXTRACTED>
  errorMessage?: string
  onRemove: () => void
  onClick?: () => void
}

/**
 * A pending direct-upload row on the Add Files surface: a PDF icon, the filename, and the
 * upload/processing progress line. Mirrors the med-chron asset pending-upload card.
 */
export default function TimelineFileCard(props: TimelineFileCardProps) {
  const { fileName, status, errorMessage, onClick } = props

  const FileIcon = fileName.toLowerCase().endsWith('.pdf') ? FilePdf : Document

  // Allow clicking when onClick handler exists, status is PROCESSING / COMPLETED, and is a PDF file.
  const isClickable = Boolean(
    onClick && [TimelineFileStatus.PROCESSING, TimelineFileStatus.COMPLETED].includes(status) && fileName.toLowerCase().endsWith('.pdf')
  )

  const renderLeftSection = () => {
    switch (status) {
      case TimelineFileStatus.UPLOADING:
      case TimelineFileStatus.PROCESSING:
        return (
          <div className="w-16 h-full flex items-center justify-center px-3 border-r rounded-l">
            <CircularProgressContinuousSized size={24} thickness={3} showTrack={true} />
          </div>
        )
      case TimelineFileStatus.COMPLETED:
        return (
          <div className="w-16 h-full flex items-center justify-center px-3 border-r rounded-l">
            <FileIcon className="w-6 h-6 text-brand-neutral-800" />
          </div>
        )
      case TimelineFileStatus.ERROR:
        return (
          <div className="w-16 h-full flex items-center justify-center px-3 border-r rounded-l bg-red-50">
            <FileIcon className="w-6 h-6 text-red-600" />
          </div>
        )
    }
  }

  const renderStatus = () => {
    switch (status) {
      case TimelineFileStatus.UPLOADING:
        return <p className="text-xs font-medium italic text-brand-neutral-600">Uploading...</p>
      case TimelineFileStatus.PROCESSING:
        return <p className="text-xs font-medium italic text-brand-neutral-600">Processing...</p>
      case TimelineFileStatus.COMPLETED:
        return (
          <div className="flex items-center gap-1 text-green-800">
            <CheckCircle className="w-3.5 h-3.5" />
            <p className="text-xs font-medium">Completed</p>
          </div>
        )
      case TimelineFileStatus.ERROR:
        return (
          <div className="flex items-center gap-1">
            <AlertErrorCircle className="w-3.5 h-3.5 text-red-800" />
            <p className="text-xs font-medium text-red-800">{errorMessage || 'Document could not be read'}</p>
          </div>
        )
    }
  }

  const rowContent = (
    <>
      {/* Left icon section */}
      {renderLeftSection()}

      {/* Main content area */}
      <div className="flex-1 flex flex-col gap-1 justify-center px-3 pr-12 min-w-0">
        <p className="text-[13px] font-medium text-brand-neutral-950 truncate">{fileName}</p>
        {renderStatus()}
      </div>
    </>
  )
  const rowClassName = clsx('h-full w-full flex items-center rounded-[5px] text-left', isClickable && 'cursor-pointer hover:bg-brand-neutral-100')

  return (
    <div className="h-16 flex items-center rounded-[5px] border relative bg-white">
      {isClickable ? (
        <button type="button" onClick={onClick} className={rowClassName}>
          {rowContent}
        </button>
      ) : (
        <div className={rowClassName}>{rowContent}</div>
      )}

      <IconButton
        icon={<XCircle />}
        variant="plain"
        color="neutral"
        size="md"
        onClick={props.onRemove}
        wrapperClassName="absolute right-1 top-1/2 -translate-y-1/2"
        aria-label={`Remove ${fileName}`}
      />
    </div>
  )
}
